import { courses } from "@lache/courses";
import { cleanPlayerName } from "@lache/relay/names";
import { RECENT_TIMES_SIZE, type RecentTime } from "@lache/relay/recent-times";
import { formatTime } from "./scores";

const REFRESH_MS = 15_000;
const SHORT_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const FULL_DATE = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });
const SCROLL_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  "Space",
  "Enter",
]);

interface RecentTimesSource {
  readonly enabled: boolean;
  recentTimes(): Promise<RecentTime[]>;
}

interface Row {
  el: HTMLDivElement;
  name: HTMLSpanElement;
  value: HTMLSpanElement;
  course: HTMLSpanElement;
  falls: HTMLSpanElement;
  date: HTMLTimeElement;
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  el.className = className;
  return el;
}

function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}

function age(date: number, now: number): string {
  const minutes = Math.max(0, Math.floor((now - date) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)} hr ago`;
  if (minutes < 10080) return `${Math.floor(minutes / 1440)}d ago`;
  return SHORT_DATE.format(date);
}

/** A bounded, independently scrolling feed. No polling survives a closed menu or a hidden page. */
export class RecentTimes {
  readonly el = element("section", "recent-times");
  private readonly list = element("div", "recent-times-list");
  private readonly status = element("div", "recent-times-status");
  private readonly rows = new Map<string, Row>();
  private readonly touches = new Set<number>();
  private deferred: RecentTime[] | null = null;
  private visible = false;
  private pageHidden = false;
  private disposed = false;
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: Promise<void> | null = null;

  constructor(
    parent: HTMLElement,
    private readonly source: RecentTimesSource,
  ) {
    const title = element("h2", "recent-times-title");
    title.textContent = "Latest times";
    this.el.setAttribute("aria-label", "Latest times");
    this.el.hidden = true;
    this.list.tabIndex = 0;
    this.list.setAttribute("role", "list");
    this.list.setAttribute("aria-label", "Recent finishes");
    this.list.addEventListener("pointerdown", this.onTouchStart);
    this.list.addEventListener("keydown", (event) => {
      // Keep native keyboard scrolling from also navigating or activating the left-hand menu.
      if (SCROLL_KEYS.has(event.code)) event.stopPropagation();
    });
    this.status.setAttribute("role", "status");
    this.status.textContent = source.enabled ? "Loading…" : "Latest times unavailable.";
    this.el.append(title, this.list, this.status);
    parent.appendChild(this.el);
    document.addEventListener("visibilitychange", this.sync);
    window.addEventListener("pagehide", this.onPageHide);
    window.addEventListener("pageshow", this.onPageShow);
    window.addEventListener("pointerup", this.onTouchEnd);
    window.addEventListener("pointercancel", this.onTouchEnd);
    window.addEventListener("blur", this.onDragCancel);
    window.addEventListener("lacheviewportchange", this.onDragCancel);
  }

  setVisible(visible: boolean): void {
    if (this.disposed || visible === this.visible) return;
    this.visible = visible;
    this.el.hidden = !visible;
    this.sync();
  }

  private get active(): boolean {
    return this.visible && !document.hidden && !this.pageHidden && !this.disposed && this.source.enabled;
  }

  private readonly onPageHide = (): void => {
    this.pageHidden = true;
    this.sync();
  };

  private readonly onPageShow = (): void => {
    this.pageHidden = false;
    this.sync();
  };

  private readonly sync = (): void => {
    this.generation++;
    this.clearTimer();
    if (this.active) void this.refresh();
    else this.clearTouches();
  };

  private readonly onTouchStart = (event: PointerEvent): void => {
    if (event.pointerType === "touch" && this.active) this.touches.add(event.pointerId);
  };

  private readonly onTouchEnd = (event: PointerEvent): void => {
    if (!this.touches.delete(event.pointerId) || this.touches.size) return;
    // Window receives the release after the menu's rotated-drag handler has ended its swipe.
    this.flushDeferred();
  };

  private readonly onDragCancel = (): void => {
    this.touches.clear();
    this.flushDeferred();
  };

  private flushDeferred(): void {
    const entries = this.deferred;
    this.deferred = null;
    if (entries && this.active) this.render(entries);
  }

  private clearTouches(): void {
    this.touches.clear();
    this.deferred = null;
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  refresh(): Promise<void> {
    if (!this.active) return Promise.resolve();
    if (this.pending) return this.pending;
    this.clearTimer();
    const generation = this.generation;
    if (!this.rows.size) setText(this.status, "Loading…");
    this.pending = Promise.resolve()
      .then(() => (this.active && generation === this.generation ? this.source.recentTimes() : null))
      .then((entries) => {
        if (!entries || !this.active || generation !== this.generation) return;
        // Moving rows during a drag would invalidate setupMenuScroll's starting scroll position.
        if (this.touches.size) this.deferred = entries.slice(0, RECENT_TIMES_SIZE);
        else this.render(entries);
      })
      .catch(() => {
        if (this.active && generation === this.generation) {
          setText(this.status, this.rows.size ? "Couldn't refresh." : "Couldn't load times.");
        }
      })
      .finally(() => {
        this.pending = null;
        if (!this.active) return;
        // A close/reopen or page resume during the request needs a fresh response, never overlap.
        if (generation !== this.generation) void this.refresh();
        else this.timer = setTimeout(() => void this.refresh(), REFRESH_MS);
      });
    return this.pending;
  }

  private render(entries: RecentTime[]): void {
    const top = this.list.scrollTop;
    const anchor =
      top > 1
        ? (Array.from(this.list.children).find((child) => {
            const row = child as HTMLElement;
            return row.offsetTop + row.offsetHeight > top;
          }) as HTMLElement | undefined)
        : undefined;
    const anchorOffset = anchor ? anchor.offsetTop - top : 0;
    const keep = new Set<string>();
    const now = Date.now();
    for (const entry of entries.slice(0, RECENT_TIMES_SIZE)) {
      if (keep.has(entry.id)) continue;
      keep.add(entry.id);
      let row = this.rows.get(entry.id);
      if (!row) {
        const el = element("div", "recent-time");
        el.dataset.id = entry.id;
        el.setAttribute("role", "listitem");
        row = {
          el,
          name: element("span", "recent-name"),
          value: element("span", "recent-time-value"),
          course: element("span", "recent-course"),
          falls: element("span", "recent-falls"),
          date: element("time", "recent-date"),
        };
        el.append(row.name, row.value, row.course, row.falls, row.date);
        this.rows.set(entry.id, row);
      }
      const course = courses[entry.course] as { title?: string } | undefined;
      const date = entry.achievedAt ?? entry.date;
      setText(row.name, cleanPlayerName(entry.name));
      setText(row.value, formatTime(entry.time));
      setText(row.course, course?.title ?? entry.course);
      setText(row.falls, `${entry.falls} ${entry.falls === 1 ? "fall" : "falls"}`);
      setText(row.date, age(date, now));
      row.date.dateTime = new Date(date).toISOString();
      row.date.title = FULL_DATE.format(date);
      this.list.appendChild(row.el);
    }
    for (const [id, row] of this.rows) {
      if (keep.has(id)) continue;
      row.el.remove();
      this.rows.delete(id);
    }
    // Native anchoring varies while existing nodes move; hold the same visible finish explicitly.
    this.list.scrollTop = anchor?.isConnected ? anchor.offsetTop - anchorOffset : top;
    setText(this.status, this.rows.size ? "" : "No times yet.");
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.generation++;
    this.clearTimer();
    this.clearTouches();
    this.list.removeEventListener("pointerdown", this.onTouchStart);
    document.removeEventListener("visibilitychange", this.sync);
    window.removeEventListener("pagehide", this.onPageHide);
    window.removeEventListener("pageshow", this.onPageShow);
    window.removeEventListener("pointerup", this.onTouchEnd);
    window.removeEventListener("pointercancel", this.onTouchEnd);
    window.removeEventListener("blur", this.onDragCancel);
    window.removeEventListener("lacheviewportchange", this.onDragCancel);
    this.el.remove();
    this.rows.clear();
  }
}
